<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <span class="panel-title">
          <i class="fa fa-file-contract"></i>
          Support Contract{{ client.name ? ' — ' + client.name : '' }}
        </span>
        <button class="btn-back" @click="goBack()">
          <i class="fa fa-arrow-left"></i> Back
        </button>
      </div>

      <!-- ============ LIST VIEW ============ -->
      <template v-if="mode === 'list'">
        <div class="toolbar">
          <button class="btn-outline" @click="openItemsModal()">
            <i class="fa fa-cog"></i> Manage Items
          </button>
          <button class="btn-new" @click="startNewContract()">
            <i class="fa fa-plus"></i> New Contract
          </button>
        </div>

        <div class="state-box" v-if="loadingData">
          <i class="fa fa-spinner fa-spin"></i> Loading…
        </div>
        <div class="state-box" v-else-if="contracts.length === 0">
          <i class="fa fa-file-contract"></i> No support contracts yet
        </div>

        <template v-else>
          <div class="list-header">
            <div>Start Date</div>
            <div>End Date</div>
            <div>Duration</div>
            <div>Items</div>
            <div style="text-align:right;">Total</div>
            <div style="text-align:center;">Status</div>
            <div style="text-align:center;">QuickBooks</div>
            <div style="text-align:center;">Invoice #</div>
            <div></div>
          </div>

          <div class="contract-row" v-for="c in contracts" :key="c.id">
            <div>{{ c.start_date }}</div>
            <div>{{ c.end_date }}</div>
            <div>{{ c.duration_years }} yr{{ c.duration_years > 1 ? 's' : '' }}</div>
            <div>{{ c.items.length }}</div>
            <div class="cr-total">${{ contractTotalOf(c).toFixed(2) }}</div>
            <div style="text-align:center;">
              <span class="status-badge" :class="isExpired(c) ? 's-expired' : 's-active'">
                {{ isExpired(c) ? 'Expired' : 'Active' }}
              </span>
            </div>
            <div style="text-align:center;">
              <span v-if="c.is_quickbooks_synced" class="status-badge s-qb-synced">
                <i class="fa fa-check-circle"></i> Synced
              </span>
              <span v-else class="status-badge s-qb-unsynced">Not Synced</span>
            </div>
            <div class="qb-invoice-no" style="text-align:center;">{{ c.quickbooks_invoice_number || '—' }}</div>
            <div class="cr-actions">
              <button
                class="icon-btn"
                :class="{ 'is-busy': postingInvoiceId === c.id }"
                :disabled="postingInvoiceId === c.id"
                @click="postInvoiceToQuickBooks(c)"
                :title="c.is_quickbooks_synced ? 'Resync invoice with QuickBooks' : 'Post invoice to QuickBooks'"
              >
                <i :class="postingInvoiceId === c.id ? 'fa fa-spinner fa-spin' : (c.is_quickbooks_synced ? 'fa fa-sync' : 'fa fa-file-invoice')"></i>
              </button>
              <button class="icon-btn" @click="editContract(c)" title="Edit">
                <i class="fa fa-pencil-alt"></i>
              </button>
              <button class="icon-btn danger" @click="confirmDeleteContract(c.id)" title="Delete">
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </template>
      </template>

      <!-- ============ FORM VIEW ============ -->
      <template v-else>
        <div class="contract-form">

          <div class="form-grid">
            <div class="form-field">
              <label>Start Date</label>
              <input type="date" v-model="form.start_date" @change="suggestEndDate()" />
            </div>
            <div class="form-field">
              <label>Duration</label>
              <select v-model.number="form.duration_years" @change="suggestEndDate()">
                <option v-for="opt in durationOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>
            <div class="form-field">
              <label>End Date</label>
              <input type="date" v-model="form.end_date" />
            </div>
          </div>

          <div class="items-section">
            <div class="items-header">
              <span>Contract Items</span>
              <button class="btn-outline sm" @click="openItemsModal()">
                <i class="fa fa-cog"></i> Manage Items
              </button>
            </div>

            <div class="item-list-header">
              <div>Item</div>
              <div style="text-align:right;">Rate</div>
              <div style="text-align:right;">Qty</div>
              <div>Remarks</div>
              <div style="text-align:right;">Total</div>
              <div></div>
            </div>

            <div class="item-row" v-for="(row, idx) in form.items" :key="idx">
              <div class="ts-wrap" :class="{ open: itemDropdownOpenIndex === idx }" v-click-outside="() => closeItemDropdownFor(idx)">
                <div class="ts-field" @click="toggleItemDropdown(idx)">
                  <i class="fa fa-cube ts-icon"></i>
                  <input
                    class="ts-input"
                    :ref="'itemInput' + idx"
                    :value="itemDropdownOpenIndex === idx ? itemSearch : itemLabel(row)"
                    @input="itemSearch = $event.target.value"
                    @mousedown.prevent
                    placeholder="Select item"
                  />
                  <i class="fa fa-chevron-down ts-arrow" :class="{ 'ts-arrow--open': itemDropdownOpenIndex === idx }"></i>
                </div>
                <div class="ts-menu" v-if="itemDropdownOpenIndex === idx">
                  <div
                    class="ts-opt"
                    v-for="it in filteredItemOptions"
                    :key="it.id"
                    :class="{ 'ts-opt--sel': row.item_id === it.id }"
                    @mousedown.prevent="selectItemRow(idx, it.id)"
                  >{{ it.name }}</div>
                  <div class="ts-empty" v-if="!filteredItemOptions.length">No matches</div>
                </div>
              </div>
              <div class="input-wrap">
                <span class="ip">$</span>
                <input class="num-input" type="number" min="0" v-model.number="row.rate" placeholder="0.00" @wheel="$event.target.blur()" @focus="$event.target.select()" />
              </div>
              <div class="input-wrap">
                <input class="num-input" type="number" min="1" v-model.number="row.quantity" placeholder="1" @wheel="$event.target.blur()" @focus="$event.target.select()" />
              </div>
              <div class="input-wrap">
                <input class="remarks-input" type="text" v-model="row.remarks" placeholder="Remarks (optional)" maxlength="500" />
              </div>
              <div class="line-total">${{ lineTotal(row).toFixed(2) }}</div>
              <button class="icon-btn danger" @click="removeRow(idx)" title="Remove">
                <i class="fa fa-trash"></i>
              </button>
            </div>

            <button class="btn-add-row" @click="addRow()">
              <i class="fa fa-plus"></i> Add Item
            </button>
          </div>

          <div class="form-footer">
            <div class="footer-discount">
              <label>Overall Discount</label>
              <select v-model="form.discount_type">
                <option value="percentage">% Percentage</option>
                <option value="flat">$ Flat</option>
              </select>
              <div class="input-wrap">
                <span class="ip">{{ form.discount_type === 'percentage' ? '%' : '$' }}</span>
                <input class="num-input" type="number" min="0" v-model.number="form.discount_value" placeholder="0" @wheel="$event.target.blur()" @focus="$event.target.select()" />
              </div>
            </div>
            <div class="footer-totals">
              <div class="footer-row">
                <span>Subtotal</span>
                <span>${{ itemsSubtotal.toFixed(2) }}</span>
              </div>
              <div class="footer-row" v-if="discountAmount > 0">
                <span>Discount</span>
                <span>-${{ discountAmount.toFixed(2) }}</span>
              </div>
              <div class="footer-row footer-total">
                <span>Contract Total</span>
                <span>${{ contractTotal.toFixed(2) }}</span>
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button class="btn-cancel" @click="cancelForm()">Cancel</button>
            <button class="btn-save" ref="kt_save_contract" @click="saveContract()">
              <i class="fa fa-check"></i> Save Contract
            </button>
          </div>
        </div>
      </template>

    </div>

    <!-- ============ MANAGE ITEMS MODAL ============ -->
    <div class="modal-overlay" v-if="showItemsModal" @click.self="closeItemsModal()">
      <div class="modal-box">
        <div class="modal-header">
          <span><i class="fa fa-list"></i> Manage Contract Items</span>
          <button class="modal-close" @click="closeItemsModal()">&times;</button>
        </div>
        <div class="modal-body">
          <div class="new-item-row">
            <input
              type="text"
              v-model="newItemName"
              placeholder="e.g. Livescan System"
              @keyup.enter="saveItem()"
            />
            <button class="btn-add-item" @click="saveItem()">
              <i class="fa" :class="editingItemId ? 'fa-check' : 'fa-plus'"></i>
              {{ editingItemId ? 'Update' : 'Add' }}
            </button>
            <button class="btn-cancel-item" v-if="editingItemId" @click="cancelEditItem()" title="Cancel edit">
              <i class="fa fa-times"></i>
            </button>
          </div>

          <div class="state-box sm" v-if="items.length === 0">No items yet</div>
          <div class="item-list-row" v-for="it in items" :key="it.id" :class="{ 'is-editing': editingItemId === it.id }">
            <span>{{ it.name }}</span>
            <div class="item-list-actions">
              <button class="icon-btn" @click="editItem(it)" title="Edit">
                <i class="fa fa-pencil-alt"></i>
              </button>
              <button class="icon-btn danger" @click="deleteItem(it.id)" title="Delete">
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ DELETE CONFIRM MODAL ============ -->
    <div class="modal-overlay" v-if="delModal.show" @click.self="delModal.show = false">
      <div class="modal-box sm">
        <div class="modal-body confirm-body">
          <p>Delete this support contract? This cannot be undone.</p>
          <div class="form-actions">
            <button class="btn-cancel" @click="delModal.show = false">Cancel</button>
            <button class="btn-danger" @click="deleteContract()">Delete</button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import {
  FETCH_B_SUPPORT_CONTRACT_ITEMS,
  CREATE_B_SUPPORT_CONTRACT_ITEM,
  DELETE_B_SUPPORT_CONTRACT_ITEM,
  FETCH_B_SUPPORT_CONTRACT_LIST,
  CREATE_B_SUPPORT_CONTRACT,
  UPDATE_B_SUPPORT_CONTRACT,
  DELETE_B_SUPPORT_CONTRACT,
  POST_B_SUPPORT_CONTRACT_INVOICE
} from "@/core/services/store/actions.type";

const emptyForm = () => ({
  start_date: "",
  duration_years: 1,
  end_date: "",
  discount_type: "percentage",
  discount_value: 0,
  items: []
});

export default {
  data() {
    return {
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Locations", route: "client.location" },
        { id: 3, title: "Support Contract", route: "" }
      ],
      loadingData: false,
      mode: "list", // 'list' | 'form'
      client: {},
      contracts: [],
      items: [],
      durationOptions: [
        { value: 1, label: "1 Year" },
        { value: 2, label: "2 Years" },
        { value: 3, label: "3 Years" },
        { value: 5, label: "5 Years" }
      ],
      showItemsModal: false,
      newItemName: "",
      editingItemId: null,
      editingContractId: null,
      form: emptyForm(),
      delModal: { show: false, id: null },
      postingInvoiceId: null,
      itemDropdownOpenIndex: null,
      itemSearch: ""
    };
  },
  directives: {
    clickOutside: {
      bind(el, binding) {
        el._clickOutsideHandler = e => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener("mousedown", el._clickOutsideHandler);
      },
      unbind(el) {
        document.removeEventListener("mousedown", el._clickOutsideHandler);
      }
    }
  },
  computed: {
    computedEndDate() {
      if (!this.form.start_date) return "";
      const d = new Date(this.form.start_date + "T00:00:00");
      if (isNaN(d.getTime())) return "";
      d.setFullYear(d.getFullYear() + this.form.duration_years);
      return d.toISOString().slice(0, 10);
    },
    itemsSubtotal() {
      return this.form.items.reduce((sum, row) => sum + this.lineTotal(row), 0);
    },
    discountAmount() {
      return this.calcDiscountAmount(this.itemsSubtotal, this.form.discount_type, this.form.discount_value);
    },
    contractTotal() {
      return Math.max(this.itemsSubtotal - this.discountAmount, 0);
    },
    filteredItemOptions() {
      const q = this.itemSearch.toLowerCase();
      return this.items.filter(it => !q || it.name.toLowerCase().includes(q));
    }
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.loadData();
  },
  methods: {
    goBack() {
      this.$router.push({ name: "client.location" });
    },
    loadData() {
      this.loadingData = true;
      Promise.all([
        this.$store.dispatch(FETCH_B_SUPPORT_CONTRACT_ITEMS),
        this.$store.dispatch(FETCH_B_SUPPORT_CONTRACT_LIST, this.$route.params.id)
      ])
        .then(([items, data]) => {
          this.items = items || [];
          this.contracts = data.contracts || [];
          this.client = data.client || {};
          this.loadingData = false;
        })
        .catch(() => {
          this.loadingData = false;
        });
    },
    itemLabel(row) {
      const found = this.items.find(it => it.id === row.item_id);
      return found ? found.name : "";
    },
    openItemDropdown(idx) {
      this.itemDropdownOpenIndex = idx;
      this.itemSearch = "";
      this.$nextTick(() => {
        const ref = this.$refs["itemInput" + idx];
        if (ref && ref[0]) ref[0].focus();
      });
    },
    toggleItemDropdown(idx) {
      this.itemDropdownOpenIndex === idx ? this.closeItemDropdown() : this.openItemDropdown(idx);
    },
    closeItemDropdown() {
      this.itemDropdownOpenIndex = null;
      this.itemSearch = "";
    },
    closeItemDropdownFor(idx) {
      // Every row shares one v-click-outside handler; only the row that is
      // actually open may close it, otherwise opening row A while row B is
      // open would let B's outside-click check null out A's just-opened state.
      if (this.itemDropdownOpenIndex === idx) {
        this.closeItemDropdown();
      }
    },
    selectItemRow(idx, id) {
      this.form.items[idx].item_id = id;
      this.closeItemDropdown();
    },
    lineTotal(row) {
      const rate = Number(row.rate) || 0;
      const quantity = Number(row.quantity) || 0;
      return Math.max(rate * quantity, 0);
    },
    calcDiscountAmount(subtotal, discountType, discountValue) {
      const value = Number(discountValue) || 0;
      const off = discountType === "percentage" ? (subtotal * value) / 100 : value;
      return Math.min(Math.max(off, 0), subtotal);
    },
    contractTotalOf(contract) {
      const subtotal = contract.items.reduce((sum, i) => sum + this.lineTotal(i), 0);
      const discount = this.calcDiscountAmount(subtotal, contract.discount_type, contract.discount_value);
      return Math.max(subtotal - discount, 0);
    },
    isExpired(contract) {
      return new Date(contract.end_date + "T00:00:00") < new Date(new Date().toDateString());
    },
    startNewContract() {
      this.editingContractId = null;
      this.form = emptyForm();
      this.mode = "form";
    },
    editContract(c) {
      this.editingContractId = c.id;
      this.form = {
        start_date: c.start_date,
        duration_years: c.duration_years,
        end_date: c.end_date,
        discount_type: c.discount_type || "percentage",
        discount_value: c.discount_value || 0,
        items: c.items.map(i => ({
          item_id: i.item ? i.item.id : null,
          rate: i.rate,
          quantity: i.quantity || 1,
          remarks: i.remarks || ""
        }))
      };
      this.mode = "form";
    },
    cancelForm() {
      this.mode = "list";
    },
    suggestEndDate() {
      // Re-suggests End Date from Start Date + Duration whenever either is
      // actively changed. End Date stays a normal editable field otherwise,
      // so a manual override survives until start date/duration are touched
      // again.
      if (this.computedEndDate) this.form.end_date = this.computedEndDate;
    },
    addRow() {
      this.form.items.push({ item_id: null, rate: 0, quantity: 1, remarks: "" });
    },
    removeRow(idx) {
      this.form.items.splice(idx, 1);
    },
    saveContract() {
      if (!this.form.start_date) {
        this.$toastr.e("Please select a start date");
        return;
      }
      if (!this.form.end_date) {
        this.$toastr.e("Please select an end date");
        return;
      }
      if (this.form.items.length === 0) {
        this.$toastr.e("Please add at least one item");
        return;
      }
      if (this.form.items.some(i => !i.item_id)) {
        this.$toastr.e("Please select an item for every row");
        return;
      }

      const ref = this.$refs.kt_save_contract;
      if (ref) ref.disabled = true;

      const payload = {
        start_date: this.form.start_date,
        duration_years: this.form.duration_years,
        end_date: this.form.end_date,
        discount_type: this.form.discount_type,
        discount_value: this.form.discount_value || 0,
        items: this.form.items
      };

      const request = this.editingContractId
        ? this.$store.dispatch(UPDATE_B_SUPPORT_CONTRACT, {
            contract_id: this.editingContractId,
            contract: payload
          })
        : this.$store.dispatch(CREATE_B_SUPPORT_CONTRACT, {
            location_id: this.$route.params.id,
            contract: payload
          });

      request
        .then(data => {
          this.$toastr.s(data.msg);
          if (ref) ref.disabled = false;
          this.mode = "list";
          this.loadData();
        })
        .catch(() => {
          this.$toastr.e("Something went wrong");
          if (ref) ref.disabled = false;
        });
    },
    confirmDeleteContract(id) {
      this.delModal = { show: true, id };
    },
    deleteContract() {
      this.$store.dispatch(DELETE_B_SUPPORT_CONTRACT, this.delModal.id).then(data => {
        this.$toastr.s(data.msg);
        this.delModal.show = false;
        this.loadData();
      });
    },
    postInvoiceToQuickBooks(contract) {
      if (this.postingInvoiceId) return;
      this.postingInvoiceId = contract.id;
      this.$store
        .dispatch(POST_B_SUPPORT_CONTRACT_INVOICE, contract.id)
        .then(data => {
          this.$toastr.s(data.msg);
          this.postingInvoiceId = null;
          this.loadData();
        })
        .catch(err => {
          this.postingInvoiceId = null;
          this.$toastr.e((err && err.msg) || "Failed to post invoice to QuickBooks");
        });
    },
    openItemsModal() {
      this.showItemsModal = true;
    },
    closeItemsModal() {
      this.showItemsModal = false;
      this.cancelEditItem();
    },
    editItem(it) {
      this.editingItemId = it.id;
      this.newItemName = it.name;
    },
    cancelEditItem() {
      this.editingItemId = null;
      this.newItemName = "";
    },
    saveItem() {
      const name = this.newItemName.trim();
      if (!name) return;
      this.$store
        .dispatch(CREATE_B_SUPPORT_CONTRACT_ITEM, { id: this.editingItemId || 0, name })
        .then(() => {
          this.cancelEditItem();
          this.$store.dispatch(FETCH_B_SUPPORT_CONTRACT_ITEMS).then(items => {
            this.items = items || [];
          });
        });
    },
    deleteItem(id) {
      this.$store
        .dispatch(DELETE_B_SUPPORT_CONTRACT_ITEM, id)
        .then(() => {
          this.items = this.items.filter(i => i.id !== id);
          if (this.editingItemId === id) this.cancelEditItem();
        })
        .catch(err => {
          this.$toastr.e((err && err.msg) || "Something went wrong");
        });
    }
  }
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$green:  #276749;
$red:    #e53e3e;
$border: #dde2ec;

.page-wrap {
  padding: 8px 0 80px;
  font-family: $font;
}

.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  overflow: visible;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #eef2fb;
  border-bottom: 1px solid #dde4f5;
  border-radius: 12px 12px 0 0;

  .panel-title {
    font-size: 13px;
    font-weight: 700;
    color: $blue;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .btn-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 14px;
    background: #fff;
    color: #4a5568;
    border: 1.5px solid $border;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    font-family: $font;
    cursor: pointer;
    transition: all 0.15s;

    &:hover { border-color: $blue; color: $blue; }
  }
}

.toolbar {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px;
  border-bottom: 1px solid #f0f2f8;
  background: #fafbfd;
}

.btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  background: #fff;
  color: $blue;
  border: 1.5px solid $blue;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover { background: #f0f9ff; }
  &.sm { height: 28px; padding: 0 10px; font-size: 11px; }
}

.btn-new {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 16px;
  background: $green;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover { opacity: 0.9; }
}

/* ── List ── */
.list-header, .contract-row {
  display: grid;
  grid-template-columns: 100px 100px 70px 55px 90px 85px 90px 100px 116px;
  align-items: center;
  padding: 10px 20px;
  gap: 8px;
}

.list-header {
  background: #fafbfd;
  border-bottom: 2px solid #eef0f8;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #a0aab8;
}

.contract-row {
  border-bottom: 1px solid #f5f7fa;
  font-size: 12.5px;
  color: $dark;

  &:hover { background: #f8fbff; }
}

.cr-total {
  text-align: right;
  font-family: monospace;
  font-weight: 700;
}

.cr-actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;

  &.s-active  { background: #dcfce7; color: $green; }
  &.s-expired { background: #fee2e2; color: $red; }
  &.s-qb-synced   { background: #dcfce7; color: $green; gap: 4px; }
  &.s-qb-unsynced { background: #e2e8f0; color: #718096; }
}

.qb-invoice-no {
  font-size: 11.5px;
  font-family: monospace;
  color: #7a8499;
}

.icon-btn {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid $border;
  background: #fff;
  color: #7a8499;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover { border-color: $blue; color: $blue; }
  &.danger:hover { border-color: $red; color: $red; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:disabled:hover { border-color: $border; color: #7a8499; }
  &.is-busy { color: $blue; border-color: $blue; }
}

.state-box {
  text-align: center;
  padding: 56px 24px;
  color: #a0aab8;
  font-size: 13px;

  &.sm { padding: 16px; font-size: 12px; }
}

/* ── Form ── */
.contract-form {
  padding: 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  max-width: 640px;
  margin-bottom: 24px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #a0aab8;
  }

  input, select {
    height: 36px;
    border: 1.5px solid $border;
    border-radius: 8px;
    padding: 0 10px;
    font-size: 13px;
    font-family: $font;
    color: $dark;

    &:focus { outline: none; border-color: $blue; }
  }

  select {
    padding: 0 28px 0 10px;
    background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23a0aab8'/%3E%3C/svg%3E") no-repeat right 10px center;
    appearance: none;
    cursor: pointer;
  }
}

.items-section {
  border: 1px solid #e8edf5;
  border-radius: 10px;
  margin-bottom: 20px;
}

.items-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: #f5f8fc;
  border-bottom: 1px solid #e8edf5;
  border-radius: 10px 10px 0 0;
  font-size: 12px;
  font-weight: 700;
  color: $dark;
}

.item-list-header, .item-row {
  display: grid;
  grid-template-columns: 1.8fr 1fr 0.7fr 1.4fr 1fr 36px;
  align-items: center;
  padding: 8px 16px;
  gap: 10px;
}

.item-list-header {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #a0aab8;
  border-bottom: 1px solid #f0f2f8;
}

.item-row {
  border-bottom: 1px solid #f5f7fa;
}

/* ── Type-and-select (item picker) ── */
.ts-wrap {
  position: relative;
  min-width: 0;
}

.ts-field {
  display: flex;
  align-items: center;
  height: 32px;
  border: 1.5px solid $border;
  border-radius: 6px;
  background: #fff;
  padding: 0 8px;
  gap: 6px;
  cursor: pointer;
  transition: border-color 0.2s;

  .ts-wrap.open & {
    border-color: $blue;
    box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
  }
}

.ts-icon {
  font-size: 11px;
  color: #a0aab8;
  flex-shrink: 0;
}

.ts-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 12px;
  font-family: $font;
  color: $dark;
  background: transparent;
  min-width: 0;
  cursor: pointer;

  &::placeholder { color: #b0bac9; }
}

.ts-arrow {
  font-size: 10px;
  color: #a0aab8;
  flex-shrink: 0;
  transition: transform 0.2s;

  &--open { transform: rotate(180deg); }
}

.ts-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(13, 27, 62, 0.1);
  z-index: 500;
  max-height: 220px;
  overflow-y: auto;
}

.ts-opt {
  padding: 8px 12px;
  font-size: 12px;
  color: $dark;
  cursor: pointer;
  transition: background 0.12s;

  &:hover { background: #f0f7fc; }

  &--sel { background: #eef7fb; color: $blue; font-weight: 600; }
}

.ts-empty {
  padding: 10px 12px;
  font-size: 12px;
  color: #a0aab8;
  text-align: center;
}

.input-wrap {
  display: flex;
  align-items: center;
  height: 32px;
  border: 1.5px solid $border;
  border-radius: 6px;
  overflow: hidden;

  &:focus-within { border-color: $blue; }

  .ip {
    height: 100%;
    padding: 0 6px;
    background: #f5f7fa;
    border-right: 1px solid #e2e8f0;
    color: #a0aab8;
    font-size: 11px;
    font-weight: 600;
    display: flex;
    align-items: center;
  }

  .num-input {
    flex: 1;
    border: none;
    outline: none;
    height: 100%;
    padding: 0 8px;
    font-size: 12px;
    text-align: right;
    min-width: 0;
    font-family: $font;

    &[type=number] { -moz-appearance: textfield; appearance: textfield; }
    &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  }

  .remarks-input {
    flex: 1;
    border: none;
    outline: none;
    height: 100%;
    padding: 0 8px;
    font-size: 12px;
    text-align: left;
    min-width: 0;
    font-family: $font;
  }
}

.line-total {
  text-align: right;
  font-family: monospace;
  font-weight: 700;
  color: $dark;
}

.btn-add-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 10px 16px;
  background: #fafbfd;
  border: none;
  border-top: 1px dashed #e2e8f0;
  border-radius: 0 0 10px 10px;
  color: $blue;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover { background: #f0f9ff; }
}

.form-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.footer-discount {
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #a0aab8;
    margin-right: 2px;
  }

  select {
    height: 34px;
    border: 1.5px solid $border;
    border-radius: 6px;
    padding: 0 26px 0 8px;
    font-size: 12px;
    font-family: $font;
    background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23a0aab8'/%3E%3C/svg%3E") no-repeat right 8px center;
    appearance: none;
    cursor: pointer;

    &:focus { outline: none; border-color: $blue; }
  }

  .input-wrap { width: 120px; height: 34px; }
}

.footer-totals {
  min-width: 220px;
  text-align: right;
}

.footer-row {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding: 3px 0;
  font-size: 12.5px;
  color: #7a8499;
  font-family: monospace;

  &.footer-total {
    margin-top: 4px;
    padding-top: 8px;
    border-top: 1px solid #e8edf5;
    font-size: 15px;
    font-weight: 700;
    color: $green;
    font-family: $font;
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-cancel {
  height: 36px;
  padding: 0 18px;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 8px;
  color: #4a5568;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover { border-color: #a0aab8; }
}

.btn-save {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 22px;
  background: $green;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
}

.btn-danger {
  height: 36px;
  padding: 0 18px;
  background: $red;
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover { opacity: 0.9; }
}

/* ── Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13, 27, 62, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: #fff;
  border-radius: 12px;
  width: 420px;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(13, 27, 62, 0.3);

  &.sm { width: 360px; }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: #eef2fb;
  border-bottom: 1px solid #dde4f5;
  font-size: 13px;
  font-weight: 700;
  color: $dark;

  i { margin-right: 6px; color: $blue; }
}

.modal-close {
  background: none;
  border: none;
  font-size: 20px;
  line-height: 1;
  color: #a0aab8;
  cursor: pointer;

  &:hover { color: $dark; }
}

.modal-body {
  padding: 16px 18px;
  overflow-y: auto;
}

.confirm-body {
  p { font-size: 13px; color: #4a5568; margin-bottom: 16px; }
}

.new-item-row {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;

  input {
    flex: 1;
    height: 36px;
    border: 1.5px solid $border;
    border-radius: 8px;
    padding: 0 10px;
    font-size: 13px;
    font-family: $font;

    &:focus { outline: none; border-color: $blue; }
  }
}

.btn-add-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  background: $blue;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;

  &:hover { opacity: 0.9; }
}

.btn-cancel-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 8px;
  color: #7a8499;
  cursor: pointer;

  &:hover { border-color: $red; color: $red; }
}

.item-list-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 4px;
  border-bottom: 1px solid #f5f7fa;
  font-size: 13px;
  color: $dark;

  &:last-child { border-bottom: none; }
  &.is-editing { background: #f0f9ff; }
}

.item-list-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
